import React, { useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Download, FileSpreadsheet, FileText } from "lucide-react";
import { toast } from "sonner";

/**
 * Universal export menu. `module` should match the backend module name
 * (assets, subscriptions, audits, invoices, vendors, tickets, users, companies).
 */
export default function ExportMenu({ module, params = {}, label = "Export" }) {
  const [busy, setBusy] = useState(false);

  const download = async (format) => {
    setBusy(true);
    try {
      const token = localStorage.getItem("pgac_token");
      const qs = new URLSearchParams({ format, ...params }).toString();
      const url = `${BACKEND_URL}/api/exports/${module}?${qs}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const a = document.createElement("a");
      const objectUrl = URL.createObjectURL(blob);
      a.href = objectUrl;
      a.download = `pgac_${module}_export.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(objectUrl);
      toast.success(`${module} exported as ${format.toUpperCase()}`);
    } catch (e) { toast.error("Export failed"); }
    finally { setBusy(false); }
  };

  const downloadTemplate = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem("pgac_token");
      const r = await fetch(`${BACKEND_URL}/api/exports/${module}/template`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const a = document.createElement("a");
      const u = URL.createObjectURL(blob);
      a.href = u; a.download = `pgac_${module}_template.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(u);
    } catch { toast.error("Template not available"); }
    finally { setBusy(false); }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid={`export-menu-${module}`} disabled={busy}
                className="border border-black h-10 px-3 uppercase tracking-wider text-xs font-head bg-white hover:bg-black hover:text-white inline-flex items-center gap-1">
          <Download size={14}/>{busy ? "…" : label}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuItem data-testid={`export-${module}-xlsx`} onClick={() => download("xlsx")}>
          <FileSpreadsheet size={14} className="mr-2"/> Excel (.xlsx)
        </DropdownMenuItem>
        <DropdownMenuItem data-testid={`export-${module}-csv`} onClick={() => download("csv")}>
          <FileText size={14} className="mr-2"/> CSV
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid={`template-${module}`} onClick={downloadTemplate}>
          <Download size={14} className="mr-2"/> Import template
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
